/**
 * Editor header: the row fields a `PATCH` writes, the dirty indicator, and the publish gate's
 * legible reason. Discipline follows from the type and is never asked for.
 */
import { computed } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { Grade } from './enums'

import { gradeOptions, withCount } from '@shared/lib/format'

import { AssignmentStatus } from './enums'
import { useAssignmentEditorStore } from './store'
import {
  getAssignmentStatusColor,
  getAssignmentStatusLabel,
  getAssignmentTypeLabel,
} from './assignment-template-options'

interface UseAssignmentHeaderReturn {
  refTitle: WritableComputedRef<string | number>
  refGrade: WritableComputedRef<Grade | undefined>
  refTypeLabel: ComputedRef<string>
  refStatusLabel: ComputedRef<string>
  refStatusColor: ComputedRef<AppBadgeColor>
  refIsDirty: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  refDirtyText: ComputedRef<string>
  refSaveText: ComputedRef<string>
  refCanPublish: ComputedRef<boolean>
  /** `null` exactly when publishing is allowed. */
  refPublishBlockedReason: ComputedRef<string | null>
  refIsPublished: ComputedRef<boolean>
  refIsArchived: ComputedRef<boolean>
  gradeSelectOptions: AppSelectOption<Grade>[]
}

export const useAssignmentHeader = (): UseAssignmentHeaderReturn => {
  const store = useAssignmentEditorStore()

  const refTitle = computed<string | number>({
    get: () => store.getTitle,
    set: value => store.setTitle(String(value ?? '')),
  })

  const refGrade = computed<Grade | undefined>({
    get: () => store.getGrade ?? undefined,
    set: value => store.setGrade(value ?? null),
  })

  // Shown, not offered: the type is fixed at creation (a change is a 422).
  const refTypeLabel = computed<string>(() => (store.getType ? getAssignmentTypeLabel(store.getType) : '—'))

  const refStatusLabel = computed<string>(() =>
    store.getStatus ? getAssignmentStatusLabel(store.getStatus) : '—')

  const refStatusColor = computed<AppBadgeColor>(() =>
    store.getStatus ? getAssignmentStatusColor(store.getStatus) : 'slate')

  const refIsDirty = computed<boolean>(() => store.isDirty)
  const refIsSaving = computed<boolean>(() => store.isSaving)

  const refDirtyText = computed<string>(() => (refIsDirty.value ? 'Unsaved changes' : 'Saved'))

  const refSaveText = computed<string>(() => (refIsSaving.value ? 'Saving…' : 'Save'))

  const refIsPublished = computed<boolean>(() => store.getStatus === AssignmentStatus.Published)
  const refIsArchived = computed<boolean>(() => store.getStatus === AssignmentStatus.Archived)

  const refCanPublish = computed<boolean>(() => store.isPublishable)

  const refPublishBlockedReason = computed<string | null>(() => {
    if (store.isPublishable) {
      return null
    }
    if (!store.hasDocument) {
      return 'There is no document to publish yet.'
    }
    // Not-yet-validated gets no sentence: the Validate button beside Publish already says what to do.
    if (!store.hasValidationReport) {
      return null
    }

    const count = store.getValidationIssues.length

    return `Fix ${withCount(count, { one: 'problem', other: 'problems' })} before publishing.`
  })

  return {
    refTitle,
    refGrade,
    refTypeLabel,
    refStatusLabel,
    refStatusColor,
    refIsDirty,
    refIsSaving,
    refDirtyText,
    refSaveText,
    refCanPublish,
    refPublishBlockedReason,
    refIsPublished,
    refIsArchived,
    gradeSelectOptions: gradeOptions,
  }
}
